import type { AxiosResponse } from "axios"

import { apiClient } from "../lib/axios"
import type {
  CreateGroupRequestProps,
  CreateRoleRequestProps,
  EditUserRequest,
  PatchedGroupRequest,
} from "../types/custom/Permissions"
import type { CustomRegistrationInput } from "../types/custom/authentication"
import type { CustomRegistration } from "../types/generated/authentication/CustomRegistration"
import type { CustomTokenObtainPair } from "../types/generated/authentication/CustomTokenObtainPair"
import type { CustomTokenRefresh } from "../types/generated/authentication/CustomTokenRefresh"
import type { GoogleOauth2RegisterResponse } from "../types/generated/authentication/GoogleOauth2RegisterResponse"
import type { GroupRetrieve } from "../types/generated/authentication/GroupRetrieve"
import type { LogoutRequest } from "../types/generated/authentication/LogoutRequest"
import type { Organization } from "../types/generated/authentication/Organization"
import type { OrganizationInvitation } from "../types/generated/authentication/OrganizationInvitation"
import type { OrganizationInvitationBulkRequest } from "../types/generated/authentication/OrganizationInvitationBulkRequest"
import type { OrganizationInvitationBulkResponse } from "../types/generated/authentication/OrganizationInvitationBulkResponse"
import type { PatchedOrganization } from "../types/generated/authentication/PatchedOrganization"
import type { PatchedUser } from "../types/generated/authentication/PatchedUser"
import type { Role } from "../types/generated/authentication/Role"
import type { SendEmailReset } from "../types/generated/authentication/SendEmailReset"
import type { TokenObtainPairResponse } from "../types/generated/authentication/TokenObtainPairResponse"
import type { User } from "../types/generated/authentication/User"
import type { UserManagementLog } from "../types/generated/authentication/UserManagementLog"
import { getHostnames } from "../utils/genericHelpers"

const { auth } = getHostnames()

export const SidAPI = {
  login: async ({ email, password }: CustomTokenObtainPair): Promise<AxiosResponse<TokenObtainPairResponse>> => {
    return apiClient.postResource(
      auth,
      "/api/auth/login/",
      { email, password },
      { withXSRFToken: true, withCredentials: true },
    )
  },
  googleLogin: async (credential: string): Promise<AxiosResponse<TokenObtainPairResponse>> => {
    return apiClient.postResource(
      auth,
      "/api/auth/oauth/google/token/",
      {
        id_token: credential,
        product: "konan",
      },
      { withXSRFToken: true, withCredentials: true },
    )
  },
  logOut: async ({ all_devices }: LogoutRequest): Promise<AxiosResponse<void>> => {
    return apiClient.postResource(
      auth,
      "/api/auth/logout/",
      { all_devices },
      { withCredentials: true, withXSRFToken: true },
    )
  },
  refreshToken: async (refresh?: string): Promise<AxiosResponse<CustomTokenRefresh>> => {
    return apiClient.postResource(
      auth,
      "/api/auth/token/refresh/",
      { refresh },
      { withXSRFToken: true, withCredentials: true },
    )
  },
  signup: async ({
    firstName,
    lastName,
    email,
    password,
    rePassword,
    position,
  }: CustomRegistrationInput): Promise<AxiosResponse<CustomRegistration>> => {
    return apiClient.postResource(auth, "/api/auth/signup/", {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      re_password: rePassword,
      position,
    })
  },
  googleSignup: async (idToken: string): Promise<AxiosResponse<GoogleOauth2RegisterResponse>> => {
    return apiClient.postResource(
      auth,
      "/api/auth/oauth/google/signup/",
      {
        id_token: idToken,
        product: "konan",
        login_on_register: true,
      },
      { withXSRFToken: true, withCredentials: true },
    )
  },
  resendVerificationLink: async (email: string): Promise<AxiosResponse<void>> => {
    return apiClient.postResource(auth, `/api/auth/verify/resend/`, {
      email: email,
    })
  },
  resetPassword: async ({ email }: SendEmailReset): Promise<SendEmailReset> => {
    // api api call actually returns on content when successful, only status 204
    const { data } = await apiClient.postResource(
      auth,
      "/api/auth/password/reset/",
      { email },
      {
        headers: {
          Authorization: "",
        },
      },
    )
    return data
  },
  createOrganization: async (props: {
    name: string
    description?: string
    logo?: File
  }): Promise<AxiosResponse<Organization>> => {
    const { name, description, logo } = props

    const formData = new FormData()
    formData.append("name", name)
    if (description) formData.append("description", description)
    if (logo) formData.append("logo", logo)

    return apiClient.postResource(auth, "/api/organizations/", formData)
  },
  fetchOrganization: async (uid: string): Promise<AxiosResponse<Organization>> => {
    return apiClient.fetchResource(auth, `/api/organizations/${uid}/`)
  },
  updateOrganization: async (props: PatchedOrganization): Promise<AxiosResponse<Organization>> => {
    const { uid, name, description, logo } = props

    const formData = new FormData()
    if (name) formData.append("name", name)
    if (typeof description === "string") formData.append("description", description)
    if (logo !== undefined) formData.append("logo", logo === null ? "" : logo)

    return apiClient.patchResource(auth, `/api/organizations/${uid}/`, formData)
  },
  leaveOrganization: async (): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/user/organization/`)
  },
  deleteOrganization: async (uid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/user/organizations/${uid}/`)
  },
  removeUser: async (uid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/organizations/users/${uid}/`)
  },
  editUser: async (props: EditUserRequest): Promise<AxiosResponse> => {
    const { userId, role, groups } = props

    return apiClient.patchResource(auth, `/api/organizations/users/${userId}/`, {
      role,
      groups,
    })
  },
  inviteUsers: async ({
    orgId,
    emails,
    expiry_date,
    role,
    groups,
  }: {
    orgId: string
  } & OrganizationInvitationBulkRequest): Promise<AxiosResponse<OrganizationInvitationBulkResponse>> => {
    return apiClient.postResource(auth, `/api/organizations/${orgId}/invitations/`, {
      emails: emails,
      role: role,
      groups: groups,
      expiry_date: expiry_date,
    })
  },
  fetchPendingInvites: async (uid: string): Promise<AxiosResponse<OrganizationInvitation[]>> => {
    return apiClient.fetchResource(auth, `/api/organizations/${uid}/invitations/`)
  },
  removeInvitation: async (uid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/organizations/invitations/${uid}/`)
  },
  fetchRoles: async (organization_uuid: string): Promise<AxiosResponse<Role[]>> => {
    return apiClient.fetchResource(auth, `/api/organizations/${organization_uuid}/roles/`)
  },
  createRole: async (props: CreateRoleRequestProps & { organization_uuid: string }): Promise<AxiosResponse<Role>> => {
    return apiClient.postResource(auth, `/api/organizations/${props.organization_uuid}/roles/`, {
      ...props,
    })
  },
  fetchRole: async (role_uuid: string): Promise<AxiosResponse<Role>> => {
    return apiClient.fetchResource(auth, `/api/organizations/roles/${role_uuid}/`)
  },
  deleteRole: async (role_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/organizations/roles/${role_uuid}/`)
  },
  updateRole: async (props: CreateRoleRequestProps & { role_uuid: string }): Promise<AxiosResponse<Role>> => {
    return apiClient.patchResource(auth, `/api/organizations/roles/${props.role_uuid}/`, {
      ...props,
    })
  },
  fetchGroups: async (organization_uuid: string): Promise<AxiosResponse<GroupRetrieve[]>> => {
    return apiClient.fetchResource(auth, `/api/organizations/${organization_uuid}/groups/`)
  },
  createGroup: async (
    props: CreateGroupRequestProps & { organization_uuid: string },
  ): Promise<AxiosResponse<GroupRetrieve>> => {
    const { organization_uuid } = props
    return apiClient.postResource(auth, `/api/organizations/${organization_uuid}/groups/`, {
      ...props,
    })
  },
  deleteGroup: async (group_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(auth, `/api/organizations/groups/${group_uuid}/`)
  },
  updateGroup: async (props: PatchedGroupRequest): Promise<AxiosResponse> => {
    const { uid } = props
    return apiClient.patchResource(auth, `/api/organizations/groups/${uid}/`, {
      ...props,
    })
  },
  fetchSIDPermissions: async (): Promise<AxiosResponse> => {
    const { data } = await apiClient.fetchResource(auth, `/api/permissions/`)
    return data
  },
  fetchUserManagementChangeHistory: async (organization_uuid: string): Promise<AxiosResponse<UserManagementLog[]>> => {
    return apiClient.fetchResource(auth, `/api/organizations/${organization_uuid}/user-management-logs/`)
  },
  fetchUser: async (): Promise<AxiosResponse<User>> => {
    return apiClient.fetchResource(auth, `/api/user/`)
  },
  updateUser: async (props: PatchedUser): Promise<AxiosResponse<User>> => {
    return apiClient.patchResource(auth, `/api/user/`, {
      ...props,
    })
  },
  fetchCurrentOrganization: async (): Promise<AxiosResponse<Organization>> => {
    return apiClient.fetchResource(auth, `/api/user/organization/`)
  },
}
